import React, { useCallback, useEffect, useRef, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMapEvents, useMap, Tooltip } from 'react-leaflet';
import L from 'leaflet';
import { OpenStreetMapProvider } from 'leaflet-geosearch';
import 'leaflet/dist/leaflet.css';
import ReactDOMServer from "react-dom/server";
import { IoLocation } from "react-icons/io5";
import { extractLocationData, reverseGeocode, slugify, useUserActivity } from '../../Public/Explore/services';
import { useNavigate } from 'react-router-dom';

// Fix for default markers in react-leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom icon
const createCustomIcon = (color: string = '#ff2424') => 
  L.divIcon({
    html: ReactDOMServer.renderToString(
      <div style={{
        backgroundColor: "white",
        borderRadius: "50%",
        width: "40px",
        height: "40px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow: "0 2px 6px rgba(0, 0, 0, 0.3)",
      }}>
        <IoLocation size={34} color={color} />
      </div>
    ),
    className: "",
    iconSize: [40, 40],
    iconAnchor: [20, 40],
    popupAnchor: [0, -40],
  });

export interface LocationData {
  lng: number;
  lat: number;
  address?: string;
  area?: string;
  city?: string;
  restaurant?: {
    id: string;
    name: string;
    rating: number;
    cover: string;
  }
}

export interface MapComponentProps {
  locationData?: LocationData;
  locations?: LocationData[];
  setLocationData?: (location: LocationData) => void;
  height?: string;
  searchable?: boolean;
  interactive?: boolean;
}

function MapClickHandler({ onMapClick }: { onMapClick: (lat: number, lng: number) => void }) {
  useMapEvents({
    click: (e) => {
      onMapClick(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

function MapUpdater({ center }: { center: [number, number] }) {
  const map = useMap();
  
  useEffect(() => {
    map.setView(center, map.getZoom());
  }, [center, map]);

  return null;
}

const MapComponent: React.FC<MapComponentProps> = ({
  locationData,
  locations = [],
  setLocationData,
  height = '400px',
  searchable = true,
  interactive = true,
}) => {
  const [mapCenter, setMapCenter] = useState<[number, number]>(() => {
    if (locations.length > 0) {
      return [locations[0].lat, locations[0].lng];
    } else if (locationData?.lat && locationData?.lng) {
      return [locationData.lat, locationData.lng];
    }
    return [-1.286389, 36.817223]; // default
  });
  
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const { trackClick } = useUserActivity();
  const searchProvider = new OpenStreetMapProvider();
  const searchRef = useRef<HTMLDivElement>(null);

  const navigate = useNavigate();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Update map center when locationData changes
  useEffect(() => {
    if (locationData?.lat && locationData?.lng) {
      setMapCenter([locationData.lat, locationData.lng]);
    }
  }, [locationData?.lat, locationData?.lng]);

  const handleSearch = async (query: string) => {
    setSearchQuery(query);
    if (query.length > 2) {
      setIsSearching(true);
      try {
        const results = await searchProvider.search({ query });
        setSuggestions(results);
        setShowSuggestions(true);
      } catch (error) {
        console.error('Search error:', error);
      } finally {
        setIsSearching(false);
      }
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSuggestionSelect = (suggestion: any) => {
    const [lat, lng] = [suggestion.y, suggestion.x];
    setMapCenter([lat, lng]);
    
    if (setLocationData) {
      const {city, address, area} = extractLocationData(suggestion.label);
      setLocationData({
        lat,
        lng,
        address,
        city,
        area,
      });
    }
    
    setSearchQuery(suggestion.label);
    setShowSuggestions(false);
  };

  const handleMapClick = async (lat: number, lng: number) => {
    if (interactive && setLocationData) {
      const { address, city, area } = await reverseGeocode(lat, lng);
      setLocationData({ lat, lng, address, city, area });
    }
  };

  const handleMarkerDrag = async (e: any) => {
    if (interactive && setLocationData) {
      const { lat, lng } = e.target.getLatLng();
      const { address, area, city } = await reverseGeocode(lat, lng);
      setLocationData({ lat, lng, address, city, area });
    }
  };
  const handleMarkerClick = useCallback((id: string, name: string) => {
    {trackClick(id);
    window.location.href = `/restaurant/${id}/${slugify(name)}`;}
  }, [trackClick]);

  return (
    <div 
      className="map-component bg-white rounded-lg border border-[#FFDDA6] relative"
      style={{ 
        width: '100%', 
        height: height,
      }}
    >
      {/* Search Bar */}
      {searchable && (
        <div 
          ref={searchRef} 
          className="absolute top-3 left-3 right-3 z-[1001]"
        >
          <div className="relative z-10">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              onFocus={() => searchQuery.length > 2 && setShowSuggestions(true)}
              className="w-full pl-10 pr-4 py-3 border border-[#FFDDA6] rounded-lg focus:ring-2 focus:ring-[#FFD166] focus:border-transparent bg-white text-[#111827] text-sm shadow-lg"
              placeholder="Search for location, address, or area..."
            />

            <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#5E503F]">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>

            {isSearching && (
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                <div className="animate-spin rounded-full h-5 w-5 border-4 border-t-transparent border-[#FFD166]"></div>
              </div>
            )}

            {showSuggestions && suggestions.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-[#FFDDA6] rounded-lg shadow-xl z-20 max-h-60 overflow-y-auto">
                {suggestions.map((suggestion, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => handleSuggestionSelect(suggestion)}
                    className="w-full px-4 py-3 text-left hover:bg-[#FFF8E7] transition-colors border-b border-[#FFDDA6] last:border-b-0"
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 mt-0.5 text-[#FFD166]">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                            d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                            d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-[#111827] text-sm truncate">
                          {suggestion.label.split(',')[0]}
                        </div>
                        <div className="text-xs text-[#5E503F] truncate">
                          {suggestion.label.split(',').slice(1).join(',').trim()}
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Map Container */}
      <div className="realtive w-full h-full rounded-lg overflow-hidden">
        <MapContainer
          center={mapCenter}
          zoom={13}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom={true}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          
          {locations.length > 0 ? (
            locations.map((loc, i) => (
              <Marker
                key={i}
                position={[loc.lat, loc.lng]}
                icon={createCustomIcon()}
                eventHandlers={{
                  click: () => handleMarkerClick(loc.restaurant.id, loc.restaurant.name),
                }}
              >
                {loc.restaurant && (
                  <Tooltip direction="top" offset={[0, -40]} opacity={1}>
                    <div className="w-40">
                      {loc.restaurant?.cover && (
                        <img
                          src={loc.restaurant.cover}
                          className="w-full h-20 object-cover rounded-md"
                          alt={loc.restaurant.name}
                        />
                      )}
                      <div className="mt-1 font-semibold text-sm text-gray-800">
                        {loc.restaurant?.name}
                      </div>
                      <div className="text-yellow-500 text-xs">
                        ⭐ {loc.restaurant?.rating ?? "N/A"}
                      </div>
                    </div>
                  </Tooltip>
                )}
              </Marker>
            ))
          ) : (
            <Marker
              position={mapCenter}
              icon={createCustomIcon()}
              draggable={interactive}
              eventHandlers={{
                dragend: handleMarkerDrag,
              }}
            >
              <Popup>
                <div className="text-center p-2">
                  <div className="font-bold text-[#111827] text-sm mb-1">📍</div>
                  <p className="text-xs text-[#5E503F]">
                    Drag me or click anywhere on the map to move
                  </p>
                </div>
              </Popup>
            </Marker>
          )}
          
          {interactive && (
            <MapClickHandler onMapClick={handleMapClick} />
          )}
          
          <MapUpdater center={mapCenter} />
        </MapContainer>
      </div>
    </div>
  );
};

export default MapComponent;